import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ScrollText, CheckCircle2, Loader2, PenLine } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Non-dismissible Code of Conduct gate. Mounted globally (App.js).
// Backend independently enforces via HTTP 423 middleware — this is the UX.
export const CodeOfConductGate = () => {
  const location = useLocation();
  const [status, setStatus] = useState(null); // null | {required, unsigned_players, signed_players, version}
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState(null);
  const lastCheck = useRef(0);
  const scrollRef = useRef(null);

  const check = useCallback(async (force = false) => {
    if (localStorage.getItem('isLoggedIn') !== 'true') { setStatus(null); return; }
    if (!force && Date.now() - lastCheck.current < 30000 && status && !status.required) return;
    lastCheck.current = Date.now();
    try {
      const r = await fetch(`${API_URL}/api/coc/status`, { credentials: 'include' });
      if (r.ok) setStatus(await r.json());
    } catch (_e) { /* network — fail open, backend still enforces */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => { check(); }, [location.pathname, check]);
  useEffect(() => {
    const h = () => check(true);
    window.addEventListener('auth-changed', h);
    return () => window.removeEventListener('auth-changed', h);
  }, [check]);

  if (!status?.required) return null;

  const player = status.unsigned_players[0];
  const totalPlayers = status.unsigned_players.length + status.signed_players.length;

  const onScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) setScrolledToEnd(true);
  };

  const sign = async () => {
    setSigning(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/coc/sign`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          athlete_id: player.athlete_id,
          signer_name: signerName,
          version_id: status.version.version_id,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : (d.detail?.message || 'Could not save signature'));
      if (d.remaining_unsigned === 0) {
        window.location.reload(); // unblock — refresh page data
        return;
      }
      setAgreed(false); // next player: re-confirm agreement (scroll stays read)
      await check(true);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="coc-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="px-6 pt-5 pb-3 border-b">
          <div className="flex items-center gap-2 text-[#0B1F4B]">
            <ScrollText className="w-5 h-5" />
            <h2 className="text-lg font-bold">{status.version.title} — v{status.version.version_number}</h2>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            A signed Code of Conduct is required for each enrolled player before you can use the site.
          </p>
          {/* Per-player progress */}
          <div className="mt-2 flex flex-wrap gap-2" data-testid="coc-player-progress">
            {status.signed_players.map((p) => (
              <span key={p.athlete_id} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2.5 py-1 text-xs font-medium">
                <CheckCircle2 className="w-3 h-3" /> {p.name} — signed
              </span>
            ))}
            {status.unsigned_players.map((p, i) => (
              <span key={p.athlete_id} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${i === 0 ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-400' : 'bg-gray-100 text-gray-600'}`}>
                <PenLine className="w-3 h-3" /> {p.name}{i === 0 ? ' — signing now' : ''}
              </span>
            ))}
          </div>
        </div>

        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="flex-1 overflow-y-auto px-6 py-4 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed"
          data-testid="coc-content-scroll"
        >
          {status.version.content}
        </div>

        <div className="border-t px-6 py-4 space-y-3 bg-gray-50 rounded-b-2xl">
          {!scrolledToEnd && (
            <p className="text-xs text-amber-700 font-medium" data-testid="coc-scroll-hint">
              ↓ Please scroll to the bottom of the document to continue
            </p>
          )}
          <label className={`flex items-start gap-2 text-sm ${!scrolledToEnd ? 'opacity-50' : ''}`}>
            <Checkbox
              checked={agreed}
              disabled={!scrolledToEnd}
              onCheckedChange={(v) => setAgreed(!!v)}
              data-testid="coc-agree-checkbox"
              className="mt-0.5"
            />
            <span>
              I have read and agree to the Code of Conduct on behalf of <strong>{player.name}</strong>
              {totalPlayers > 1 && <span className="text-gray-500"> (signature {status.signed_players.length + 1} of {totalPlayers})</span>}
            </span>
          </label>
          <div>
            <Input
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Type your full name as your signature"
              disabled={!scrolledToEnd}
              className="bg-white"
              data-testid="coc-signer-name"
            />
          </div>
          {error && <p className="text-xs text-red-600" data-testid="coc-error">{error}</p>}
          <Button
            className="w-full bg-[#0B1F4B] hover:bg-[#132c66] text-white font-semibold"
            disabled={!scrolledToEnd || !agreed || signerName.trim().length < 3 || signing}
            onClick={sign}
            data-testid="coc-sign-btn"
          >
            {signing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving signature…</> : <>Sign for {player.name}</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CodeOfConductGate;
